import {Inject, Injectable} from '@angular/core';
import {
  LoginResponse,
  UserProfileData,
  WiseIdAuthService,
  FrontendToken,
  RefreshTokenRequest
} from '@solverml/api';
import {BehaviorSubject, lastValueFrom, Observable, tap, throwError} from 'rxjs';
import { UserResult } from '@rfebm/sso-api';
import {APP_BASE_HREF} from "@angular/common";
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class WiseIdAuthServiceManager {

  public readonly redirectUrl: string;
  private loggedInStatus = new BehaviorSubject<boolean>(this.isLoggedIn());

  // Nombres de las claves en Session Storage
  private readonly ACCESS_TOKEN_KEY = 'wiseIdAccessToken';
  private readonly REFRESH_TOKEN_KEY = 'wiseIdRefreshToken';

  constructor(
    private wiseIdAuthService: WiseIdAuthService,
    private router: Router,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {
    const cleanBaseHref = this.baseHref.endsWith('/') ? this.baseHref.slice(0, -1) : this.baseHref;
    this.redirectUrl = `${window.location.protocol}//${window.location.host}${cleanBaseHref}/after-login`;
  }

  /**
   * Llama al endpoint /auth/refresh-token usando el cliente de API.
   * Lee el refresh token desde sessionStorage y lo envía en el body.
   */
  public refreshToken(): Observable<FrontendToken> {

    const storedToken = this.getRefreshToken();
    if (!storedToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    // 1. Creamos el body de la petición
    const requestBody: RefreshTokenRequest = {
      refresh_token: storedToken
    };

    // 2. Llamamos al endpoint con el body
    return this.wiseIdAuthService.refreshAccessToken(requestBody).pipe(
      tap((response: FrontendToken) => {
        // 3. El endpoint devuelve AMBOS tokens nuevos
        this.saveAccessToken(response.access_token);
        this.saveRefreshToken(response.refresh_token);
      })
    );
  }


  public isAdmin(): boolean {
    const user = this.getUserSessionData();
    return (user as any)?.permission_access?.isAdmin || false;
  }

  public isInternalUser(): boolean {
    const email = (this.getUserProfileSessionData()?.profile?.email || '').trim().toLowerCase();
    return email.endsWith('@iasolver.com');
  }

  /**
   *  Ahora guarda ambos tokens desde el JSON.
   */
  public async loginWithWiseId(authorizationCode: string): Promise<void | false> {
    try {
      const response: LoginResponse = await lastValueFrom(
        this.wiseIdAuthService.loginAndGetTokens(authorizationCode,this.redirectUrl)
      );

      if (response && response.token) {
        // 1. Guardamos el Access Token
        this.saveAccessToken(response.token.access_token);
        // 2. Guardamos el Refresh Token
        this.saveRefreshToken(response.token.refresh_token);

        this.setUserProfileSessionData(response.user_profile);
        this.loggedInStatus.next(true);
      } else {
        console.error('No access token received from Wise ID.');
        return false;
      }
      return;
    } catch (error) {
      console.error('Error during Wise ID login:', error);
    }
    return false;
  }

  public setUserProfileSessionData(user: UserProfileData){
    let user_data = this.checkAdmin(user)
    sessionStorage.setItem('userProfileData', JSON.stringify(user_data));
  }

  private checkAdmin(user: UserProfileData): UserProfileData {
    if (!user.permission_access.isAdmin) {
      return user;
    }
    const adminUser = JSON.parse(JSON.stringify(user));
    adminUser.permission_access = {
      isAdmin: true,
      canAssignAdmin: user.permission_access.canAssignAdmin,
      canReadAnalysis: true,
      canReadPerformance: true,
      canReadHealth: true,
      canUploadVideo: true,
      canEditVideo: true,
    };
    return adminUser;
  }


  public getUserProfileSessionData(): UserProfileData | null {
    const userData = sessionStorage.getItem('userProfileData');
    if (userData) {
      return JSON.parse(userData) as UserProfileData;
    }
    return null;
  }

  public getAccessToken(): string | null {
    return sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  public getRefreshToken(): string | null {
    return sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  public isLoggedIn(): boolean {
    return this.getAccessToken() !== null;
  }

  public logout(redirectToLogin: boolean = true): void {
    sessionStorage.clear(); // Esto borra ambos tokens
    this.loggedInStatus.next(false);

    if (redirectToLogin) {
      this.router.navigate(['/login']);
    }
  }

  public setUserSessionData(user: UserResult): void {
    sessionStorage.setItem('wiseIdUser', JSON.stringify(user));
  }

  public getUserSessionData(): UserResult | null {
    const userData = sessionStorage.getItem('wiseIdUser');
    if (userData) {
      return JSON.parse(userData) as UserResult;
    }
    return null;
  }

  public saveAccessToken(accessToken: string): void {
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
  }

  public saveRefreshToken(refreshToken: string): void {
    sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }
}
